import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import TextLink from 'LuxKit/TextLink'
import JourneyV2DetailsModalFlight, { type SnapshotDetailsFlight } from './JourneyV2DetailsModalFlight'
import ScheduleChangeFareRules from './ScheduleChangeFareRules'
import { parseScheduleChangeDateTime } from 'lib/flights/scheduleChangePresentation'
import noop from 'lib/function/noop'

export type ScheduleChangeFareRulesSelection =
  | { rules: NonNullable<App.FlightScheduleChangeFlight['fareRules']>, urls?: never }
  | { urls: Array<string>, rules?: never }

interface Props {
  leg: App.FlightScheduleChangeLeg
  bookingFareRules?: App.FlightScheduleChange['fareRules']
  onShowFareRules: (selection: ScheduleChangeFareRulesSelection) => void
}

const inclusionTypes: ReadonlyArray<App.FareFamilyFeatureType> = [
  'carry_on_baggage', 'carry_on_bag', 'checked_baggage', 'overhead_baggage',
  'changes', 'cancellation', 'meals', 'seat_reservation', 'refunds', 'insurance',
  'lounge', 'wifi', 'power', 'usb', 'in_seat_power',
]

const snapshotJourney = { links: [] } satisfies Pick<App.Leg, 'links'>

function snapshotFlight(flight: App.FlightScheduleChangeFlight, leg: App.FlightScheduleChangeLeg): SnapshotDetailsFlight {
  const departure = parseScheduleChangeDateTime(flight.departureDateTime)
  const arrival = parseScheduleChangeDateTime(flight.arrivalDateTime)
  const airline = flight.operatingAirline.name ? flight.operatingAirline : flight.marketingAirline
  return {
    departureAirport: flight.departureAirport ?? '',
    departureAirportName: flight.departureAirportName ?? flight.departureAirport ?? '',
    arrivalAirport: flight.arrivalAirport ?? '',
    arrivalAirportName: flight.arrivalAirportName ?? flight.arrivalAirport ?? '',
    // Normalise the snapshot wall clocks into the shared component's data format.
    // eslint-disable-next-line datetime-display/no-moment-format-call
    departingDate: departure.format('YYYY-MM-DD'),
    // eslint-disable-next-line datetime-display/no-moment-format-call
    departingTime: departure.format('HH:mm'),
    // eslint-disable-next-line datetime-display/no-moment-format-call
    arrivalDate: arrival.format('YYYY-MM-DD'),
    // eslint-disable-next-line datetime-display/no-moment-format-call
    arrivalTime: arrival.format('HH:mm'),
    operatingAirline: { code: airline.code ?? '', name: airline.name ?? '', logoUrl: airline.logoUrl ?? '' },
    flightNumber: flight.flightNumber ?? '',
    aircraftType: flight.aircraftType ?? '',
    fareClass: flight.fareClass ?? leg.fareFamilyName ?? flight.cabinClass ?? '',
    bookingClass: flight.bookingClass ?? '',
    flightTime: flight.flightTime ?? (flight.flightDuration === undefined ? undefined : { hours: Math.floor(flight.flightDuration / 60), minutes: flight.flightDuration % 60 }),
  }
}

function SnapshotFlightDetails({ flight, leg, index, onShowFareRules }: { flight: App.FlightScheduleChangeFlight, leg: App.FlightScheduleChangeLeg, index: number, onShowFareRules: Props['onShowFareRules'] }) {
  const intl = useIntl()
  const displayFlight = useMemo(() => snapshotFlight(flight, leg), [flight, leg])
  const inclusions = useMemo((): App.InclusionFeatures => {
    const capturedInclusions = flight.inclusions?.length ? flight.inclusions : leg.inclusions ?? []
    return capturedInclusions.filter(inclusion => inclusion.status === 'INCLUDED').map(inclusion => ({
      description: inclusion.description,
      // Unknown API types retain their description and use the shared fallback icon.
      type: inclusionTypes.find(type => type === inclusion.type),
    }))
  }, [flight.inclusions, leg.inclusions])
  const links = useMemo(() => (leg.links ?? []).filter(link => link.key === 'fareRules'), [leg.links])
  const onShowRules = useCallback(() => {
    if (flight.fareRules?.length) {
      onShowFareRules({ rules: flight.fareRules })
    } else if (links.length) {
      onShowFareRules({ urls: links.map(link => link.value) })
    }
  }, [flight.fareRules, links, onShowFareRules])
  const hasFareRules = Boolean(flight.fareRules?.length || links.length)
  const fareRulesLink = useMemo(() => <TextLink size="caption" weight="regular" onClick={onShowRules}>{intl.formatMessage({
    id: 'sGZSYnOda2ZHpCAcAz9T',
    defaultMessage: 'View fare rules',
    description: 'Link text to view fare rules in flight details modal',
  })}
  </TextLink>, [intl, onShowRules])
  const additionalDetails = <VerticalSpacer gap={12}>
    {flight.fareDetails?.filter(fare => fare.fareFamilyName || fare.fareBasisCode).map((fare, i) => <BodyText key={`${fare.fareBasisCode}-${fare.passengerType}-${i}`} variant="medium">{fare.passengerType ? intl.formatMessage({
      id: 'ZIPW-DYiDHybSash1FCj',
      defaultMessage: '{fare} ({passengerType})',
      description: 'Captured fare description and its passenger category for a schedule change flight',
    }, { fare: fare.fareFamilyName ?? fare.fareBasisCode, passengerType: fare.passengerType }) : fare.fareFamilyName ?? fare.fareBasisCode}
    </BodyText>)}
    {flight.baggageAllowances?.filter(bag => bag.description).map((bag, i) => <BodyText key={`${bag.type}-${bag.passengerType}-${i}`} variant="medium">{bag.description}</BodyText>)}
    {flight.departureTerminal && <BodyText variant="medium">{intl.formatMessage({
      id: '5U-xF39zLqdWMm8cIWo3',
      defaultMessage: 'Departure terminal: {terminal}',
      description: 'Departure terminal for a segment in the schedule change flight details modal',
    }, { terminal: flight.departureTerminal })}
    </BodyText>}
    {flight.arrivalTerminal && <BodyText variant="medium">{intl.formatMessage({
      id: 'TGVnZXSPhtxpS20pxzNV',
      defaultMessage: 'Arrival terminal: {terminal}',
      description: 'Arrival terminal for a segment in the schedule change flight details modal',
    }, { terminal: flight.arrivalTerminal })}
    </BodyText>}
    {flight.hiddenStops.filter(stop => stop.airportCode).map((stop, i) => <BodyText key={`${stop.airportCode}-${i}`} variant="medium">{intl.formatMessage({
      id: 'm0PzrqFUVitfbcQGPOJ_',
      defaultMessage: 'Stop at {airport}',
      description: 'Intermediate airport stop within a flight in the schedule change details modal',
    }, { airport: stop.airportCode })}
    </BodyText>)}

  </VerticalSpacer>
  return <JourneyV2DetailsModalFlight journey={snapshotJourney} flight={displayFlight} inclusions={inclusions} flightIndex={index} defaultOpen={leg.flights.length === 1} enableViewFareRulesButton={hasFareRules} fareRulesLink={fareRulesLink} onShowFareRules={noop} additionalDetails={additionalDetails} />
}

function ScheduleChangeJourneyDetails({ leg, bookingFareRules, onShowFareRules }: Props) {
  const intl = useIntl()
  const first = leg.flights[0]
  const last = leg.flights[leg.flights.length - 1]
  return <VerticalSpacer gap={12}>
    {first && last && <Heading variant="heading6">{intl.formatMessage({
      id: 'k0YJYWB0UhTuPqO4kNS6',
      defaultMessage: '{departureAirport} ({departureCode}) to {arrivalAirport} ({arrivalCode})',
      description: 'Heading showing flight route in details view in the flights flow',
    }, {
      departureAirport: first.departureAirportName ?? first.departureAirport,
      departureCode: first.departureAirport,
      arrivalAirport: last.arrivalAirportName ?? last.arrivalAirport,
      arrivalCode: last.arrivalAirport,
    })}
    </Heading>}
    {leg.flights.map((flight, index) => {
      const previous = leg.flights[index - 1]
      const minutes = previous?.arrivalAirport && previous.arrivalAirport === flight.departureAirport
        ? parseScheduleChangeDateTime(flight.departureDateTime).diff(parseScheduleChangeDateTime(previous.arrivalDateTime), 'minutes')
        : undefined
      return <VerticalSpacer gap={8} key={flight.id ?? `${flight.departureAirport}-${flight.departureDateTime}-${index}`}>
        {minutes !== undefined && minutes >= 0 && <MessageBanner kind="warning" icon={<LineClockFiveIcon />}>{intl.formatMessage({
          id: 'im2eAqvC2ssKq2BCncma',
          defaultMessage: '{hours}h {minutes}m layover in {airportName} ({airportCode})',
          description: 'Stopover duration and location in flight details in the flights section',
        }, { hours: Math.floor(minutes / 60), minutes: minutes % 60, airportName: flight.departureAirportName ?? flight.departureAirport, airportCode: flight.departureAirport })}
        </MessageBanner>}
        <SnapshotFlightDetails flight={flight} leg={leg} index={index} onShowFareRules={onShowFareRules} />
      </VerticalSpacer>
    })}
    {!!bookingFareRules?.length && <VerticalSpacer gap={12}>
      <Heading variant="heading6">{intl.formatMessage({
        id: '9kYpbQDXlzh574Eh5ZY0',
        defaultMessage: 'Booking fare conditions',
        description: 'Heading for captured schedule change fare conditions that may cover multiple flight legs',
      })}
      </Heading>
      <ScheduleChangeFareRules rules={bookingFareRules} />
    </VerticalSpacer>}
  </VerticalSpacer>
}

export default ScheduleChangeJourneyDetails
